import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { submitKyc } from '../../services/authService';
import { uploadFile } from '../../services/uploadService';
import { useToast } from '../../context/ToastContext';

const ID_TYPES = [
  { value: 'national_id', label: 'National ID (NIN)' },
  { value: 'drivers_license', label: "Driver's License" },
  { value: 'international_passport', label: 'International Passport' },
  { value: 'voters_card', label: "Voter's Card" }
];

const STATUS_STYLES = {
  not_submitted: { label: 'Not submitted', className: 'text-gray-500' },
  pending: { label: 'Pending review', className: 'text-yellow-600' },
  approved: { label: 'Verified', className: 'text-green-600' },
  rejected: { label: 'Rejected', className: 'text-red-600' }
};

const ClientKycForm = () => {
  const { user, refresh } = useAuth();
  const toast = useToast();
  const [idType, setIdType] = useState(ID_TYPES[0].value);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const kyc = user?.kyc || { status: 'not_submitted' };
  const status = STATUS_STYLES[kyc.status] || STATUS_STYLES.not_submitted;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please choose an ID document to upload');
      return;
    }
    setSubmitting(true);
    try {
      const idDocumentUrl = await uploadFile(file, 'client-kyc');
      await submitKyc({ idType, idDocumentUrl });
      await refresh();
      toast.success('ID submitted for review');
      setFile(null);
      e.target.reset();
    } catch (err) {
      toast.error('Upload failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow space-y-4 max-w-lg">
      <div>
        <p className="text-sm text-gray-500 mb-1">
          For your therapist's safety and yours, we verify every client's identity before their first session.
        </p>
        <p>Status: <span className={`font-semibold ${status.className}`}>{status.label}</span></p>
        {kyc.status === 'rejected' && kyc.rejectionReason && (
          <p className="text-sm text-red-600 mt-1">Reason: {kyc.rejectionReason}</p>
        )}
      </div>

      {kyc.status !== 'approved' && (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">ID type</label>
            <select value={idType} onChange={(e) => setIdType(e.target.value)} className="w-full p-2.5 border rounded-lg">
              {ID_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Upload ID document</label>
            <input type="file" accept="image/*,.pdf" onChange={(e) => setFile(e.target.files[0] || null)} disabled={submitting} required />
          </div>
          <button type="submit" disabled={submitting}
            className="bg-primary-dark text-secondary px-4 py-2 rounded-lg hover:bg-accent-hard hover:text-primary-dark disabled:opacity-50">
            {submitting ? 'Uploading...' : kyc.status === 'not_submitted' ? 'Submit for verification' : 'Resubmit'}
          </button>
        </form>
      )}
    </div>
  );
};

export default ClientKycForm;
